import { OccViewer } from "../viewer/viewer.js";
/**
 * Animation states used by [[AnimationManager]] and defined by `AnimationManager::AnimationState` enumeration.
 */
export var AnimationState;
(function (AnimationState) {
    AnimationState[AnimationState["Unknown"] = -1] = "Unknown";
    AnimationState[AnimationState["Started"] = 0] = "Started";
    AnimationState[AnimationState["Paused"] = 1] = "Paused";
    AnimationState[AnimationState["Reset"] = 2] = "Reset";
})(AnimationState || (AnimationState = {}));
;
;
/**
 * Represents an animation for an interactive object.
 * The animation is valid if the ObjectId points to a valid object,
 * and the animation is in the manager.
 */
export class ObjectAnimation {
    constructor(theViewer, theObjId = "") {
        this._myObjId = theObjId;
        this._myViewer = theViewer;
        this._myObjMgr = theViewer.interactiveContext;
        this._myAnimMgr = theViewer.animationManager;
        if (this._myAnimMgr != null && this._myObjMgr != null
            && this._myObjMgr.areObjectsLoaded(theObjId)) {
            // If object animation does not exist, add it to the manager.
            if (theObjId != "" && !this._myAnimMgr.getIsAnimValid(theObjId)) {
                this._myAnimMgr.addObjectAnimation(theObjId);
            }
        }
    }
    /** Return True if the animation exists and is applied to a valid object. */
    get isValid() {
        return (this._myObjMgr != null && this._myObjMgr.areObjectsLoaded(this._myObjId)
            && this._myAnimMgr.getIsAnimValid(this._myObjId));
    }
    /** Return animation duration in seconds. */
    get duration() { return this._myAnimMgr.getAnimDuration(this._myObjId); }
    /** Set animation duration in seconds. */
    set duration(theDuration) {
        this._myAnimMgr.setAnimDuration(this._myObjId, theDuration);
    }
    /** Return animation position as a fraction of duration. */
    get progress() {
        return Math.max(0.0, Math.min(1.0, this._myAnimMgr.position / this.duration));
    }
    /** Set animation position as a fraction of duration. */
    set progress(theProgress) {
        this._myAnimMgr.position = this.duration * theProgress;
    }
    /** Return axes. */
    get animAxes() { return this._myAnimMgr.getAnimAxis(this._myObjId); }
    /** Set axes. */
    set animAxes(theAxes) { this._myAnimMgr.setAnimAxis(this._myObjId, theAxes); }
    /** Return animation steps - a series of AnimationStep's structure defining transformations. */
    get animationSteps() {
        const aCppVec = this._myAnimMgr.getAnimSteps(this._myObjId);
        let aPnts = [];
        for (let i = 0; i < aCppVec.size(); i++) {
            aPnts.push(aCppVec.get(i));
        }
        aCppVec.delete();
        return aPnts;
    }
    /** Set animation steps - a series of AnimationStep's structure defining transformations. */
    set animationSteps(theSteps) {
        let aCppVec = new this._myViewer.AnimationStepVector();
        for (let i = 0; i < theSteps.length; i++) {
            aCppVec.push_back(theSteps[i]);
        }
        this._myAnimMgr.setAnimSteps(this._myObjId, aCppVec);
        aCppVec.delete();
    }
    /** Return current object Transformation AnimationStep. */
    get currentTransformation() {
        return this._myAnimMgr.getCurrentTransformation(this._myObjId);
    }
    /** Pause animation. */
    pause() { this._myAnimMgr.pause(); }
    /** Resume animation. */
    resume() { this._myAnimMgr.resume(); }
    /** Restart animation. */
    restart() { this._myAnimMgr.restart(); }
    /** Stop animation. */
    stop() { this._myAnimMgr.stop(); }
    /** Reset animation */
    reset() { this._myAnimMgr.reset(); }
    /** Seek animation to specified position. */
    seek(thePos) { this._myAnimMgr.seek(thePos); }
    /** Return animation state. */
    get state() {
        return this._myAnimMgr.isPaused ? AnimationState.Paused : AnimationState.Started;
    }
}
/**
 * Update [[AnimationManager]] in OccViewer.
 * @internal
*/
OccViewer.onInit((theModule) => {
    const anAnimationManager = theModule.animationManager;
    anAnimationManager.getObjectAnimation = function (theObjId) {
        if (anAnimationManager.getIsAnimValid(theObjId)) {
            return new ObjectAnimation(theModule, theObjId);
        }
        return new ObjectAnimation(theModule);
    };
    anAnimationManager.resume = function () { this.setAnimState(AnimationState.Started, -1.0, -1.0); };
    anAnimationManager.restart = function () { this.setAnimState(AnimationState.Started, 0.0, -1.0); };
    anAnimationManager.pause = function () { this.setAnimState(AnimationState.Paused, -1.0, -1.0); };
    anAnimationManager.stop = function () { this.setAnimState(AnimationState.Paused, 0.0, -1.0); };
    anAnimationManager.reset = function () { this.setAnimState(AnimationState.Reset, 0.0, -1.0); };
    anAnimationManager.seek = function (thePos) { this.position = thePos; };
    Object.defineProperty(anAnimationManager, 'duration', {
        get: function () { return this.getAllAnimDuration(); }
    });
    Object.defineProperty(anAnimationManager, 'position', {
        get: function () { return this.getAnimPosition(); },
        set: function (thePos) { this.setAnimState(AnimationState.Unknown, thePos, -1.0); }
    });
    Object.defineProperty(anAnimationManager, 'speed', {
        get: function () { return this.getAnimSpeed(); },
        set: function (theSpeed) { this.setAnimState(AnimationState.Unknown, -1.0, theSpeed); }
    });
    Object.defineProperty(anAnimationManager, 'isPaused', {
        get: function () { return this.getAnimIsPaused(); },
        set: function (theToPause) {
            this.setAnimState(theToPause ? AnimationState.Paused : AnimationState.Started, -1.0, -1.0);
        }
    });
});
